import React, { useEffect, useState } from "react";
import { MdOutlineArrowBackIosNew, MdDone, MdClose } from "react-icons/md";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AdminAd } from "../../../types/admin-types/adminTypes";
import { ADMIN_PAGE_ADS_AD_LIMIT } from "../../../configs/limits/adLimits";
import {
  approveClassifieds,
  getClassifieds,
} from "../../../services/admin-service/adminService";
import useAuthorization from "../../../hooks/useAuthorization";
import { UserRole } from "../../../enums/UserRole";
import { toastError, toastSuccess } from "../../../utils/toast";
import { FaChevronLeft, FaChevronRight, FaEllipsisV } from "react-icons/fa";
import { formatEpochTime } from "../../../utils/timeConvertors";
import "../../../styles/css/data-table.css";

function AdminAdsPage() {
  useAuthorization([UserRole.ADMIN, UserRole.ROOT]);

  const navigate = useNavigate();
  let [searchParams, setSearchParams] = useSearchParams();
  let page = searchParams.get("page") || "1";
  let adType = searchParams.get("adType") || "newClassifieds";
  let [adList, setAdList] = useState<AdminAd[]>([]);
  let [isLastPage, setIsLastPage] = useState<boolean>(false);

  async function handleTabClick(isTabChanged: boolean = false, tabLabel: string) {
    setSearchParams({ page: isTabChanged ? "1" : page, adType: tabLabel });

    let payload = {
      limit: ADMIN_PAGE_ADS_AD_LIMIT,
      skip: 0 + (Number(page) - 1) * ADMIN_PAGE_ADS_AD_LIMIT,
    };
    try {
      const classifiedData = await getClassifieds(tabLabel, payload);
      setAdList(
        classifiedData[
          tabLabel === "newClassifieds" ? "new_classifieds" : "updated_classifieds"
        ]
      );
      setIsLastPage(classifiedData.is_last_page);
    } catch (error: any) {
      setAdList([]);
    }
  }

  async function handleActionBtn(classifiedDetails: AdminAd, btnType: string) {
    let payload = {
      classified_id: classifiedDetails.classified_id,
      verified: btnType === "approve",
    };
    let approvalStatus = btnType === "approve" ? "approved" : "disapproved";
    let toastMsg =
      adType === "newClassifieds"
        ? `You've successfully ${approvalStatus} the ad!`
        : `You've successfully ${approvalStatus} the edits!`;
    try {
      await approveClassifieds(adType, payload);
      toastSuccess(toastMsg);
      setAdList((prev) =>
        prev.filter((ad) => ad.classified_id !== classifiedDetails.classified_id)
      );
    } catch (error: any) {
      toastError(error.response.data.message);
    }
  }

  function handleNext() {
    setSearchParams({ page: (Number(page) + 1).toString(), adType });
  }

  function handlePrevious() {
    if (Number(page) - 1 > 0) {
      setSearchParams({ page: (Number(page) - 1).toString(), adType });
    }
  }

  useEffect(() => {
    handleTabClick(false, adType);
  }, [page]);

  return (
    <div className="data-page">
      <div className="container">
        <div className="data-page__card">
          {/* Header */}
          <div className="data-page__header">
            <div className="data-page__title-group">
              <button
                className="data-page__back-btn"
                onClick={() => navigate("/admin/dashboard")}
              >
                <MdOutlineArrowBackIosNew size={18} />
              </button>
              <div>
                <h4 className="data-page__title">Ads</h4>
                <p className="data-page__subtitle">Review and approve listings</p>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="data-page__tabs">
            <ul className="nav nav-tabs">
              <li className="nav-item cursor-pointer" onClick={() => handleTabClick(true, "newClassifieds")}>
                <a className={"nav-link " + (adType === "newClassifieds" ? "active" : "")}>
                  New Ads
                </a>
              </li>
              <li className="nav-item cursor-pointer" onClick={() => handleTabClick(true, "editClassifieds")}>
                <a className={"nav-link " + (adType === "editClassifieds" ? "active" : "")}>
                  Ad Edits
                </a>
              </li>
            </ul>
          </div>

          {/* Table */}
          <div className="data-table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th></th>
                  <th>Ad</th>
                  <th>Seller Name</th>
                  <th>Seller Email</th>
                  <th>Seller Approved</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {!adList?.length ? (
                  <tr>
                    <td colSpan={7} className="data-table-empty">
                      No ads to review
                    </td>
                  </tr>
                ) : (
                  adList.map((ad, index) => (
                    <tr key={ad.classified_id ?? index}>
                      <th scope="row">{index + 1}</th>
                      <td>
                        <Link to={`/ads/${ad.classified_id}`} className="listing-link">
                          {[ad.model, ad.make, ad.year].filter(Boolean).join(" ")}
                        </Link>
                      </td>
                      <td>
                        <Link to={`/seller-profile/${ad.seller_id}`} className="listing-link">
                          {ad.seller_name}
                        </Link>
                      </td>
                      <td style={{ color: "#64748b", fontSize: "0.8125rem" }}>
                        {ad.seller_email}
                      </td>
                      <td>
                        {ad.seller_approved ? (
                          <MdDone className="verified-icon--yes" size={20} />
                        ) : (
                          <MdClose className="verified-icon--no" size={20} />
                        )}
                      </td>
                      <td style={{ color: "#64748b", fontSize: "0.8125rem" }}>
                        {adType === "newClassifieds"
                          ? formatEpochTime(Number(ad?.created_at))
                          : formatEpochTime(Number(ad?.updated_at))}
                      </td>
                      <td>
                        <div className="dropdown">
                          <button
                            className="action-menu-btn"
                            type="button"
                            data-bs-toggle="dropdown"
                            aria-expanded="false"
                          >
                            <FaEllipsisV size={14} />
                          </button>
                          <ul className="dropdown-menu dropdown-menu-end">
                            <li>
                              <button
                                className="dropdown-item"
                                onClick={() => handleActionBtn(ad, "approve")}
                              >
                                Approve
                              </button>
                            </li>
                            <li>
                              <button
                                className="dropdown-item text-danger"
                                onClick={() => handleActionBtn(ad, "disapprove")}
                              >
                                Disapprove
                              </button>
                            </li>
                          </ul>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="data-page__pagination">
            <button
              className="data-page__nav-btn"
              disabled={Number(page) === 1}
              onClick={handlePrevious}
            >
              <FaChevronLeft size={11} /> Previous
            </button>
            <span className="data-page__page-info">Page {page}</span>
            <button
              className="data-page__nav-btn"
              disabled={isLastPage}
              onClick={handleNext}
            >
              Next <FaChevronRight size={11} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminAdsPage;
